// src/pages/LesenTrainingPage.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ArrowLeft, ChevronRight, Play, Pause, RotateCcw,
  CheckCircle, XCircle, Clock, BookOpen, AlignLeft, Layers, Home,
  SquareCheck, AlertCircle, Lightbulb, ArrowUpRight, ClipboardList
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import PartDisplay from '../components/practice/PartDisplay';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';
import PracticeHistoryPanel from '../components/practice/PracticeHistoryPanel';
import SampleSelectView from '../components/practice/SampleSelectView';
import ExplanationPanel from '../components/practice/ExplanationPanel';
import adminService from '../services/adminService';
import subscriptionService from '../services/subscriptionService';
import enhancedUserService from '../services/enhancedUserService';
import { gradeReadingPart } from '../utils/practiceGrading';
import { readSession, writeSession } from '../utils/sessionStore';
import freeAttemptService from '../services/freeAttemptService';
import { auth } from '../firebase';
import { marked } from 'marked';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import AhaUpsellModal from '../components/ui/AhaUpsellModal';
import { trackPaywallShown } from '../utils/conversionEvents';

// ─── STOPWATCH ─────────────────────────────────────────────────────────────────
function useStopwatch() {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (running) ref.current = setInterval(() => setElapsed(e => e + 1), 1000);
    else clearInterval(ref.current);
    return () => clearInterval(ref.current);
  }, [running]);
  const toggle = () => setRunning(r => !r);
  const reset  = () => { setRunning(false); setElapsed(0); };
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;
  return { elapsed, running, toggle, reset, fmt };
}

// ─── PART TYPE META ─────────────────────────────────────────────────────────────
const PART_ICON_COMPONENTS = {
  'matching-headings':          AlignLeft,
  'matching-headlines-opinions': AlignLeft,
  'multiple-choice':            SquareCheck,
  'dual-article':               SquareCheck,
  'dual-article-comprehension': SquareCheck,
  'article-comprehension':      SquareCheck,
  'blog-post-comprehension':    BookOpen,
  'rules-comprehension':        BookOpen,
  'matching-situations':        Layers,
  'matching-questions-to-paragraphs': Layers,
  'opinion-matching':           Layers,
  'opinion-matching-academic':  Layers,
  'opinion-matching-forum':     Layers,
  'cloze-multiple-choice':      BookOpen,
  'cloze-free-text':            BookOpen,
  'cloze-word-bank-click':      BookOpen,
  'text-reconstruction-endings': AlignLeft,
  'text-reconstruction-paragraph': AlignLeft,
  'text-reconstruction-sentence': AlignLeft,
  email:                        BookOpen,
  'email-formal':               BookOpen,
  'forum-post':                 BookOpen,
};
const PART_TYPE_LABELS = {
  'matching-headings':          'Überschriften zuordnen',
  'matching-headlines-opinions': 'Aussagen zuordnen',
  'multiple-choice':            'Multiple Choice',
  'dual-article':               'Zwei Texte',
  'dual-article-comprehension': 'Zwei Artikel · Multiple Choice',
  'article-comprehension':      'Textverständnis · Multiple Choice',
  'blog-post-comprehension':    'Blogbeitrag verstehen',
  'rules-comprehension':        'Regeln verstehen',
  'matching-situations':        'Situationen zuordnen',
  'matching-questions-to-paragraphs': 'Fragen Abschnitten zuordnen',
  'opinion-matching':           'Meinungen zuordnen',
  'opinion-matching-academic':  'Positionen zuordnen',
  'opinion-matching-forum':     'Forum-Meinungen zuordnen',
  'cloze-multiple-choice':      'Lückentext (Auswahl)',
  'cloze-free-text':            'Lückentext (Freitext)',
  'cloze-word-bank-click':      'Lückentext (Wortliste)',
  'text-reconstruction-endings': 'Textenden ergänzen',
  'text-reconstruction-paragraph': 'Abschnitte einsetzen',
  'text-reconstruction-sentence': 'Sätze einsetzen',
  email:                        'E-Mail',
  'email-formal':               'Formelle E-Mail',
  'forum-post':                 'Forenbeitrag',
};

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';
const SOFT_SURFACE =
  'rounded-2xl border border-black/10 bg-white/95 backdrop-blur-sm shadow-[0_4px_14px_rgba(15,23,42,0.05)]';
const LINE_TASK_TYPES = new Set([
  'matching-headings',
  'matching-headlines-opinions',
  'text-reconstruction-endings',
  'text-reconstruction-paragraph',
  'text-reconstruction-sentence',
]);
const MULTIPLE_CHOICE_TASK_TYPES = new Set([
  'multiple-choice',
  'dual-article',
  'dual-article-comprehension',
  'article-comprehension',
]);
const STACK_TASK_TYPES = new Set([
  'matching-situations',
  'matching-questions-to-paragraphs',
  'opinion-matching',
  'opinion-matching-academic',
  'opinion-matching-forum',
]);
const CLOZE_TASK_TYPES = new Set([
  'blog-post-comprehension',
  'cloze-free-text',
  'cloze-multiple-choice',
  'cloze-word-bank-click',
  'email',
  'email-formal',
  'forum-post',
  'rules-comprehension',
]);
const LESEN_TRAINING_CARD_STYLES = `
  @keyframes lesenCardFloat {
    0%, 100% { transform: translate3d(0, 0, 0); }
    50% { transform: translate3d(0, -3px, 0); }
  }

  @keyframes lesenCardPulse {
    0%, 100% { opacity: 0.55; transform: scale(0.96); }
    50% { opacity: 0.95; transform: scale(1.08); }
  }

  @keyframes lesenCardSweep {
    0%, 100% { opacity: 0.4; transform: translateX(0) scaleX(0.92); }
    50% { opacity: 0.95; transform: translateX(3px) scaleX(1); }
  }

  @keyframes lesenCardStack {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-2px); }
  }

  .lesen-task-card:hover .lesen-task-icon-shell {
    transform: translateY(-2px);
  }

  .lesen-task-card:hover .lesen-task-icon-core {
    transform: scale(1.04);
  }

  .lesen-task-icon-shell,
  .lesen-task-icon-core,
  .lesen-task-icon-arrow {
    transition: transform 220ms ease, color 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
  }

  .lesen-task-icon-orb {
    animation: lesenCardFloat 3.8s ease-in-out infinite;
  }

  .lesen-task-icon-line {
    animation: lesenCardSweep 2.8s ease-in-out infinite;
    transform-origin: left center;
  }

  .lesen-task-icon-chip {
    animation: lesenCardPulse 2.8s ease-in-out infinite;
  }

  .lesen-task-icon-stack {
    animation: lesenCardStack 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .lesen-task-icon-orb,
    .lesen-task-icon-line,
    .lesen-task-icon-chip,
    .lesen-task-icon-stack {
      animation: none;
    }

    .lesen-task-card:hover .lesen-task-icon-shell,
    .lesen-task-card:hover .lesen-task-icon-core {
      transform: none;
    }
  }
`;

function AnimatedBackButton({ onClick, compact = false, backLabel = 'Back' }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-xl border border-[#E1E4E8] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-lg touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35 ${
        compact ? 'min-h-[44px] min-w-[44px] p-2.5' : 'min-h-[48px] px-3.5 py-2.5'
      }`}
      aria-label={backLabel}
      type="button"
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: isHovered ? '100%' : '0%',
          background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
          transition: 'width 0.7s ease-out',
          zIndex: 0,
        }}
      />
      <ArrowLeft
        className="w-4 h-4 relative z-10 transition-colors duration-500"
        style={{ color: isHovered ? '#ffffff' : '#000000' }}
      />
      {!compact && (
        <span
          className="hidden sm:inline font-semibold text-sm relative z-10 transition-colors duration-500"
          style={{ color: isHovered ? '#ffffff' : '#000000' }}
        >
          {backLabel}
        </span>
      )}
    </button>
  );
}

const LESSON_PREFIX_BY_SECTION = {
  reading: 'lv',
  grammar: 'sb',
};

const LESEN_WRITING_TASK_TYPES = new Set([
  'email', 'email-formal', 'email-opinion', 'formal-letter', 'writing', 'essay', 'forum-post',
  'formal-complaint', 'formal-message', 'formal-response-email', 'choice-writing', 'choice-writing-essay'
]);

const LESEN_ANSWER_SHEET_TASK_TYPES = new Set([
  'matching-headings', 'multiple-choice', 'dual-article', 'dual-article-comprehension',
  'article-comprehension', 'blog-post-comprehension', 'rules-comprehension', 'matching-situations',
  'matching-questions-to-paragraphs', 'opinion-matching', 'opinion-matching-academic',
  'opinion-matching-forum', 'matching-headlines-opinions', 'cloze-multiple-choice', 'cloze-free-text',
  'cloze-word-bank-click', 'text-reconstruction-endings', 'text-reconstruction-paragraph',
  'text-reconstruction-sentence'
]);

function normalizeTrainingSections(rawData) {
  if (Array.isArray(rawData)) return rawData;
  if (Array.isArray(rawData?.sections)) return rawData.sections;
  return [];
}

function extractPartNumber(part, fallbackPartIndex) {
  const byId = String(part?.partId || '').match(/(\d+)(?!.*\d)/);
  if (byId) return parseInt(byId[1], 10);

  const byName = String(part?.partName || '').match(/(?:teil|aufgabe)\s*(\d+)/i);
  if (byName) return parseInt(byName[1], 10);

  return fallbackPartIndex + 1;
}

function buildLessonStrategyLink(provider, level, sectionId, part, partIndex) {
  const prefix = LESSON_PREFIX_BY_SECTION[sectionId];
  if (!prefix) return null;

  const partNumber = extractPartNumber(part, partIndex);
  if (!Number.isFinite(partNumber) || partNumber < 1) return null;

  return {
    dataUrl: `/data/lessons/${provider}-${level}/${prefix}_part${partNumber}.json`,
    badge: `${prefix.toUpperCase()} Teil ${partNumber}`,
  };
}

function AnimatedTaskTypeIcon({ taskType, tone = 'blue' }) {
  const Icon = PART_ICON_COMPONENTS[taskType] || BookOpen;
  const isBlue = tone === 'blue';
  const shellClasses = isBlue
    ? 'border-[#3A86FF]/18 bg-gradient-to-br from-[#EEF5FF] via-white to-[#F6F8FC] shadow-[0_10px_20px_rgba(58,134,255,0.12)]'
    : 'border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-[#F5FBF7] shadow-[0_10px_20px_rgba(16,185,129,0.12)]';
  const glowClasses = isBlue
    ? 'from-[#3A86FF]/14 via-white/90 to-transparent'
    : 'from-emerald-500/12 via-white/90 to-transparent';
  const iconClasses = isBlue ? 'text-[#275eb4]' : 'text-emerald-700';
  const accentFill = isBlue ? 'bg-[#3A86FF]/14' : 'bg-emerald-500/14';
  const accentBorder = isBlue ? 'border-[#3A86FF]/18' : 'border-emerald-500/16';
  const lineClasses = isBlue ? 'bg-[#3A86FF]/35' : 'bg-emerald-600/30';

  return (
    <div className={`lesen-task-icon-shell relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-[18px] border ${shellClasses}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${glowClasses}`} />
      <div className={`lesen-task-icon-orb absolute -right-1 -top-1 h-4 w-4 rounded-full border bg-white/90 ${accentBorder}`} />

      {LINE_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 right-2.5 z-0 space-y-1">
          <span className={`lesen-task-icon-line block h-[2px] w-5 rounded-full ${lineClasses}`} />
          <span className={`lesen-task-icon-line block h-[2px] w-7 rounded-full ${lineClasses}`} style={{ animationDelay: '120ms' }} />
          <span className={`lesen-task-icon-line block h-[2px] w-4 rounded-full ${lineClasses}`} style={{ animationDelay: '240ms' }} />
        </div>
      )}

      {MULTIPLE_CHOICE_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 z-0 flex gap-1.5">
          <span className={`lesen-task-icon-chip h-2.5 w-2.5 rounded-[6px] border ${accentFill} ${accentBorder}`} />
          <span className={`lesen-task-icon-chip h-2.5 w-2.5 rounded-[6px] border ${accentFill} ${accentBorder}`} style={{ animationDelay: '160ms' }} />
        </div>
      )}

      {STACK_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 z-0">
          <span className="block -rotate-[7deg]">
            <span className={`lesen-task-icon-stack block h-2.5 w-5 rounded-[6px] border ${accentFill} ${accentBorder}`} />
          </span>
          <span className="absolute left-1 top-1 block rotate-[5deg]">
            <span className={`lesen-task-icon-stack block h-2.5 w-5 rounded-[6px] border ${accentFill} ${accentBorder}`} style={{ animationDelay: '180ms' }} />
          </span>
        </div>
      )}

      {CLOZE_TASK_TYPES.has(taskType) && (
        <div className="absolute bottom-2.5 left-2.5 right-2.5 z-0 flex items-center gap-1.5">
          <span className={`lesen-task-icon-chip h-2 w-2 rounded-full ${accentFill}`} />
          <span className={`lesen-task-icon-line block h-[2px] w-4 rounded-full ${lineClasses}`} style={{ animationDelay: '100ms' }} />
          <span className={`lesen-task-icon-line block h-[2px] w-2.5 rounded-full ${lineClasses}`} style={{ animationDelay: '220ms' }} />
        </div>
      )}

      <div className="lesen-task-icon-core relative z-10 flex items-center justify-center">
        <Icon className={`h-6 w-6 ${iconClasses}`} />
      </div>
    </div>
  );
}

// ─── STOPWATCH WIDGET ──────────────────────────────────────────────────────────
function Stopwatch({ sw, t }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={sw.toggle}
        className={`min-h-[48px] inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 font-bold text-sm transition-all duration-300 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40 ${
          sw.running ? 'border-orange-400 bg-orange-50 text-orange-700'
                     : 'border-black/10 bg-white text-gray-700 hover:border-[#3A86FF]/50 hover:text-gray-900'
        }`}
        aria-label={sw.running ? 'Timer pausieren' : 'Timer starten'}
      >
        {sw.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        <Clock className="w-4 h-4" />
        <span className="font-mono tabular-nums">{sw.fmt(sw.elapsed)}</span>
      </button>
      {sw.elapsed > 0 && !sw.running && (
        <button
          onClick={sw.reset}
          className="min-h-[48px] min-w-[48px] inline-flex items-center justify-center rounded-xl border-2 border-black/10 text-gray-400 hover:text-gray-700 hover:border-gray-400 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
          title={t('button.reset', { defaultValue: 'Reset' })}
          aria-label={t('button.reset', { defaultValue: 'Reset' })}
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ─── RESULT BADGE ──────────────────────────────────────────────────────────────
function ResultBadge({ userAnswer, correctAnswer, isCorrect }) {
  if (isCorrect) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-100 border border-emerald-400 font-black text-emerald-900 text-xs sm:text-sm shadow-[0_4px_12px_rgba(16,185,129,0.2)]">
        <CheckCircle className="w-3.5 h-3.5" />
        <span className="tracking-wide uppercase text-[10px]">Richtig</span>
        <span>{userAnswer?.toUpperCase()}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 sm:gap-2 flex-wrap">
      {userAnswer ? (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 border border-rose-300 font-black text-rose-900 text-xs sm:text-sm">
          <XCircle className="w-3.5 h-3.5" />
          {userAnswer.toUpperCase()}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 border border-gray-300 font-bold text-gray-500 text-xs sm:text-sm">Keine Antwort</span>
      )}
      <span className="text-[11px] font-bold text-gray-400">-&gt;</span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-100 border border-emerald-400 font-black text-emerald-900 text-xs sm:text-sm">
        <CheckCircle className="w-3.5 h-3.5" />
        {correctAnswer?.toUpperCase()}
      </span>
    </span>
  );
}


// ─── RESULTS: MATCHING HEADINGS ────────────────────────────────────────────────
function MatchingHeadingsResult({ part, answers }) {
  const headings = part.content?.headings || [];
  const texts    = part.content?.texts    || [];
  const qs       = (part.questions || []).filter(q => !q.isExample);

  // Map questionNumber → question
  const qByNum = qs.reduce((acc, q) => { acc[q.questionNumber] = q; return acc; }, {});

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Headings bank */}
      <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
        <h3 className="text-base font-bold text-gray-900 border-b border-black/10 pb-2 mb-3">Überschriften</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {headings.map(h => {
            const usedByQ = qs.find(q => q.correctAnswer === h.id);
            const isCorrectAnswer = !!usedByQ;
            return (
              <li key={h.id} className={`text-sm flex gap-2 ${isCorrectAnswer ? 'font-semibold text-emerald-800' : 'text-gray-600'}`}>
                <span className="font-black min-w-[20px]">{h.id.toUpperCase()})</span>
                <span>{h.text}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Texts with inline results */}
      <div className="space-y-5">
        {texts.map(txt => {
          const q = qByNum[parseInt(txt.id, 10)] || qByNum[txt.id];
          if (!q) return null;
          const userAnswer = answers[q.questionId];
          const isCorrect  = userAnswer === q.correctAnswer;
          const correctH   = headings.find(h => h.id === q.correctAnswer);

          return (
            <div
              key={txt.id}
              className={`rounded-2xl overflow-hidden border-2 shadow-[0_10px_22px_rgba(15,23,42,0.07)] ${isCorrect ? 'border-emerald-400' : 'border-rose-300'}`}
            >
              {/* Result header */}
              <div className={`flex items-center justify-between px-4 py-2.5 ${isCorrect ? 'bg-emerald-50/80' : 'bg-rose-50/80'}`}>
                <span className="font-black text-gray-900">Text {txt.id}</span>
                <ResultBadge userAnswer={userAnswer} correctAnswer={q.correctAnswer} isCorrect={isCorrect} />
              </div>
              {/* Text content */}
              <div className="bg-white p-5">
                <p className="text-sm text-gray-800 leading-relaxed">{txt.text}</p>
                {!isCorrect && correctH && (
                  <p className="mt-3 text-xs font-semibold text-emerald-700 border-t border-emerald-100 pt-2">
                    Richtige Überschrift: <span className="font-black">{correctH.id.toUpperCase()})</span> {correctH.text}
                  </p>
                )}
                <ExplanationPanel explanation={q.explanation} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS: MULTIPLE CHOICE (also dual-article, article-comprehension) ───────
function MultipleChoiceResult({ part, answers }) {
  const qs    = (part.questions || []).filter(q => !q.isExample);
  const texts = part.content?.texts || [];
  const mainText = part.content?.mainText || '';

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Single main text */}
      {mainText && (
        <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
          <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed"
               dangerouslySetInnerHTML={{ __html: marked.parse(mainText, { breaks: true }) }} />
        </div>
      )}

      {/* Multiple texts (dual-article etc.) */}
      {texts.map(txt => (
        <div key={txt.id} className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
          {txt.title && <h3 className="font-bold text-gray-900 mb-3 text-base">{txt.title}</h3>}
          <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed whitespace-pre-wrap"
               dangerouslySetInnerHTML={{ __html: marked.parse(txt.content || txt.text || '', { breaks: true }) }} />
        </div>
      ))}

      {/* Questions */}
      <div className="space-y-4">
        {qs.map(q => {
          const userAnswer = answers[q.questionId];
          const isCorrect  = userAnswer === q.correctAnswer;

          return (
            <div
              key={q.questionId}
              className={`rounded-2xl overflow-hidden border-2 shadow-[0_10px_20px_rgba(15,23,42,0.06)] ${isCorrect ? 'border-emerald-400' : 'border-rose-300'}`}
            >
              <div className={`px-4 py-2.5 flex items-start justify-between gap-3 ${isCorrect ? 'bg-emerald-50/80' : 'bg-rose-50/80'}`}>
                <p className="font-semibold text-gray-900 text-sm">
                  <span className="font-black mr-2">{q.questionNumber}.</span>
                  {q.questionText}
                </p>
                <ResultBadge userAnswer={userAnswer} correctAnswer={q.correctAnswer} isCorrect={isCorrect} />
              </div>
              <div className="bg-white p-4 space-y-2">
                {(q.options || []).map(opt => {
                  const isThisCorrect  = opt.id === q.correctAnswer;
                  const isThisSelected = opt.id === userAnswer;
                  let cls = 'flex items-start gap-3 p-3 rounded border text-sm ';
                  if (isThisCorrect)                       cls += 'border-emerald-400 bg-emerald-100 text-emerald-950 font-semibold';
                  else if (isThisSelected && !isCorrect)   cls += 'border-rose-300 bg-rose-50 text-rose-900';
                  else                                     cls += 'border-black/15 bg-white text-gray-600';

                  return (
                    <div key={opt.id} className={cls}>
                      <span className="font-black text-xs mt-0.5 min-w-[16px]">{opt.id.toUpperCase()}</span>
                      <span className="flex-1">{opt.text}</span>
                      {isThisCorrect && <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />}
                      {isThisSelected && !isCorrect && <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />}
                    </div>
                  );
                })}
                <ExplanationPanel explanation={q.explanation} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS: MATCHING SITUATIONS ─────────────────────────────────────────────
function MatchingSituationsResult({ part, answers }) {
  const situations = part.content?.situations || [];
  const texts      = part.content?.texts || part.content?.advertisements || [];
  const qs         = (part.questions || []).filter(q => !q.isExample);
  const qByNum     = qs.reduce((acc, q) => { acc[q.questionNumber] = q; return acc; }, {});

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Texts / Ads */}
      <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
        <h3 className="text-base font-bold text-gray-900 border-b border-black/10 pb-2 mb-4">Anzeigen / Texte</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {texts.map(txt => (
            <div key={txt.id} className="border border-black/10 bg-gray-50 rounded-xl p-3 text-sm">
              <span className="font-black text-gray-900 mr-1">{txt.id.toUpperCase()})</span>
              {/* Ads are authored as `text` in some papers and `content` in others. */}
              <span className="text-gray-700">{txt.text || txt.content}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Situations with inline results */}
      <div className="space-y-3">
        {situations.map(sit => {
          const q = qByNum[sit.id] || qByNum[parseInt(sit.id, 10)];
          if (!q) return null;
          const userAnswer = answers[q.questionId];
          const isCorrect  = userAnswer === q.correctAnswer;
          const correctTxt = texts.find(t => t.id === q.correctAnswer);

          return (
            <div
              key={sit.id}
              className={`rounded-2xl overflow-hidden border-2 shadow-[0_10px_22px_rgba(15,23,42,0.07)] ${isCorrect ? 'border-emerald-400' : 'border-rose-300'}`}
            >
              <div className={`flex items-start justify-between gap-3 px-4 py-3 ${isCorrect ? 'bg-emerald-50/80' : 'bg-rose-50/80'}`}>
                <div className="flex items-start gap-3 flex-1">
                  <span className="font-black text-gray-900 text-lg min-w-[28px]">{sit.id}</span>
                  <p className="text-sm text-gray-800 leading-relaxed">{sit.text}</p>
                </div>
                <ResultBadge userAnswer={userAnswer} correctAnswer={q.correctAnswer} isCorrect={isCorrect} />
              </div>
              {!isCorrect && correctTxt && (
                <div className="bg-white px-5 py-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-emerald-700">
                    Richtige Anzeige <span className="font-black">{q.correctAnswer.toUpperCase()})</span>: {correctTxt.text || correctTxt.content}
                  </p>
                </div>
              )}
              {!isCorrect && q.correctAnswer === 'x' && (
                <div className="bg-white px-5 py-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-emerald-700">Keine passende Anzeige (X)</p>
                </div>
              )}
              <div className="bg-white px-5 pb-4">
                <ExplanationPanel explanation={q.explanation} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS: CLOZE MULTIPLE CHOICE ───────────────────────────────────────────
function ClozeMultipleChoiceResult({ part, answers }) {
  const rawText = part.content?.text || part.content?.mainText || '';
  const qs      = (part.questions || []).filter(q => !q.isExample);

  // Render inline blanks
  const blankRegex = /_{2,}\s*\((\d+)\)/g;
  const segments = [];
  let lastIdx = 0;
  const re = new RegExp(blankRegex.source, 'g');
  let match;
  while ((match = re.exec(rawText)) !== null) {
    if (match.index > lastIdx) segments.push({ type: 'text', value: rawText.slice(lastIdx, match.index) });
    segments.push({ type: 'blank', blankNumber: parseInt(match[1], 10) });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < rawText.length) segments.push({ type: 'text', value: rawText.slice(lastIdx) });

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Text with inline filled blanks */}
      <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
        <p className="text-sm text-gray-800 leading-loose whitespace-pre-wrap">
          {segments.map((seg, i) => {
            if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
            const q = qs.find(q => q.blankNumber === seg.blankNumber);
            if (!q) return <span key={i} className="font-bold text-gray-400">({seg.blankNumber})</span>;
            const userAnswer = answers[q.questionId];
            const isCorrect  = userAnswer === q.correctAnswer;
            return (
              <span key={i} className="inline-flex items-center mx-0.5">
                {/* User answer, green if correct, red strikethrough if wrong */}
                {userAnswer && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-xs border ${
                    isCorrect ? 'bg-emerald-100 border-emerald-400 text-emerald-900'
                              : 'bg-rose-50 border-rose-300 text-rose-900'
                  }`}>
                    {isCorrect ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    {userAnswer.toUpperCase()}) {(q.options || []).find(o => o.id === userAnswer)?.text}
                  </span>
                )}
                {/* Correct answer, shown when wrong OR no answer given */}
                {!isCorrect && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 ml-1 rounded font-bold text-xs border bg-emerald-100 border-emerald-400 text-emerald-900">
                    <CheckCircle className="w-3 h-3" />
                    {q.correctAnswer.toUpperCase()}) {(q.options || []).find(o => o.id === q.correctAnswer)?.text}
                  </span>
                )}
              </span>
            );
          })}
        </p>
      </div>

      {/* Answer grid per question */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {qs.map(q => {
          const userAnswer = answers[q.questionId];
          const isCorrect  = userAnswer === q.correctAnswer;
          return (
            <div key={q.questionId} className={`rounded-2xl border-2 p-3 shadow-[0_8px_18px_rgba(15,23,42,0.05)] ${isCorrect ? 'border-emerald-400 bg-emerald-50/60' : 'border-rose-300 bg-rose-50/60'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-black text-gray-900 text-sm">({q.blankNumber})</span>
                <ResultBadge userAnswer={userAnswer} correctAnswer={q.correctAnswer} isCorrect={isCorrect} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(q.options || []).map(opt => {
                  const isThisCorrect  = opt.id === q.correctAnswer;
                  const isThisSelected = opt.id === userAnswer;
                  let cls = 'px-2 py-1 rounded text-xs font-semibold border ';
                  if (isThisCorrect)                     cls += 'bg-emerald-100 border-emerald-500 text-emerald-900';
                  else if (isThisSelected && !isCorrect) cls += 'bg-rose-50 border-rose-300 text-rose-900';
                  else                                   cls += 'bg-white border-black/15 text-gray-500';
                  return (
                    <span key={opt.id} className={cls}>
                      {opt.id.toUpperCase()}) {opt.text}
                    </span>
                  );
                })}
              </div>
              <ExplanationPanel explanation={q.explanation} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS: CLOZE WORD BANK ─────────────────────────────────────────────────
function ClozeWordBankResult({ part, answers }) {
  const rawText  = part.content?.text || '';
  const wordBank = part.content?.wordBank || [];
  const qs       = (part.questions || []).filter(q => !q.isExample);
  const qByNum   = qs.reduce((acc, q) => { acc[q.blankNumber] = q; return acc; }, {});

  const blankRegex = /(_{5}\s\(\d+\)|\(\d+\))/g;
  const segments = [];
  let lastIdx = 0;
  const re = new RegExp(blankRegex.source, 'g');
  let match;
  while ((match = re.exec(rawText)) !== null) {
    if (match.index > lastIdx) segments.push({ type: 'text', value: rawText.slice(lastIdx, match.index) });
    const num = parseInt(match[0].match(/\d+/)[0], 10);
    segments.push({ type: 'blank', blankNumber: num });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < rawText.length) segments.push({ type: 'text', value: rawText.slice(lastIdx) });

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      {/* Word bank */}
      {wordBank.length > 0 && (
        <div className={`${ELEVATED_SURFACE} p-4 animate-fade-in-up`}>
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Wortliste</h3>
          <div className="flex flex-wrap gap-2">
            {wordBank.map((w, i) => (
              <span key={i} className="px-3 py-1 border border-black/10 bg-gray-50 text-sm font-semibold text-gray-700 rounded-xl">{w}</span>
            ))}
          </div>
        </div>
      )}

      {/* Text with inline filled blanks */}
      <div className={`${ELEVATED_SURFACE} p-5 animate-fade-in-up`}>
        <p className="text-sm text-gray-800 leading-loose whitespace-pre-wrap">
          {segments.map((seg, i) => {
            if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
            const q = qByNum[seg.blankNumber];
            if (!q) return <span key={i} className="font-bold text-gray-400">({seg.blankNumber})</span>;
            const userAnswer = answers[q.questionId];
            const isCorrect  = userAnswer === q.correctAnswer;
            return (
              <span key={i} className="inline-flex items-center gap-1 mx-0.5">
                {/* User answer, green if correct, red strikethrough if wrong */}
                {userAnswer && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-xs border ${
                    isCorrect ? 'bg-emerald-100 border-emerald-400 text-emerald-900'
                              : 'bg-rose-50 border-rose-300 text-rose-900'
                  }`}>
                    {isCorrect ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    {userAnswer}
                  </span>
                )}
                {/* Correct answer, shown when wrong OR no answer given */}
                {!isCorrect && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-xs border bg-emerald-100 border-emerald-400 text-emerald-900">
                    <CheckCircle className="w-3 h-3" />
                    {q.correctAnswer}
                  </span>
                )}
              </span>
            );
          })}
        </p>
      </div>

      {/* Per-blank answer breakdown with explanations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {qs.map(q => {
          const userAnswer = answers[q.questionId];
          const isCorrect  = userAnswer === q.correctAnswer;
          return (
            <div key={q.questionId} className={`rounded-2xl border-2 p-3 shadow-[0_8px_18px_rgba(15,23,42,0.05)] ${isCorrect ? 'border-emerald-400 bg-emerald-50/60' : 'border-rose-300 bg-rose-50/60'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-black text-gray-900 text-sm">({q.blankNumber})</span>
                <ResultBadge userAnswer={userAnswer} correctAnswer={q.correctAnswer} isCorrect={isCorrect} />
              </div>
              <ExplanationPanel explanation={q.explanation} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── RESULTS ROUTER ────────────────────────────────────────────────────────────
function TrainingResultsView({ part, answers }) {
  const t = part.taskType;
  if (t === 'matching-headings')                  return <MatchingHeadingsResult       part={part} answers={answers} />;
  if (t === 'matching-situations')                return <MatchingSituationsResult      part={part} answers={answers} />;
  if (t === 'cloze-multiple-choice')              return <ClozeMultipleChoiceResult    part={part} answers={answers} />;
  if (t === 'cloze-word-bank-click')              return <ClozeWordBankResult          part={part} answers={answers} />;
  // multiple-choice, dual-article-comprehension, article-comprehension, and most MC types:
  return <MultipleChoiceResult part={part} answers={answers} />;
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
const LesenTrainingPage = ({ examData, onBack, subscriptionStatus, isLoadingSubscription }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const { examSlug } = useParams();
  const sw = useStopwatch();
  const lessonStrategyPath = examSlug ? `/exams/${examSlug}/lesson-strategy` : '/lesson-strategy';
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [blockedProbeNumber, setBlockedProbeNumber] = useState(null);
  const [pendingExitNav, setPendingExitNav] = useState(null);

  const handleAhaDismiss = () => {
    setShowPremiumModal(false);
    if (typeof pendingExitNav === 'function') pendingExitNav();
    setPendingExitNav(null);
  };
  const handleAhaUpgrade = () => { setShowPremiumModal(false); setPendingExitNav(null); };
  const [resolvedSubscriptionStatus, setResolvedSubscriptionStatus] = useState(subscriptionStatus ?? null);
  const [isResolvingSubscription, setIsResolvingSubscription] = useState(false);

  // Keep local status aligned with parent prop while still allowing a direct refresh in this page.
  useEffect(() => {
    if (subscriptionStatus) {
      setResolvedSubscriptionStatus(subscriptionStatus);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    let cancelled = false;
    const uid = auth.currentUser?.uid;

    if (!uid) {
      return () => {
        cancelled = true;
      };
    }

    const refreshSubscriptionStatus = async () => {
      try {
        setIsResolvingSubscription(true);
        const latestStatus = await subscriptionService.getSubscriptionStatus(uid, { disableSyncWrite: true });
        if (!cancelled) {
          setResolvedSubscriptionStatus(latestStatus);
        }
      } catch (error) {
        console.error('Error refreshing Lesen subscription status:', error);
        if (!cancelled) {
          setResolvedSubscriptionStatus(prev => prev || subscriptionStatus || subscriptionService.getDefaultFreeStatus());
        }
      } finally {
        if (!cancelled) {
          setIsResolvingSubscription(false);
        }
      }
    };

    refreshSubscriptionStatus();

    const handleFocus = () => {
      refreshSubscriptionStatus();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocus);
    }

    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleFocus);
      }
    };
  }, [subscriptionStatus]);

  const effectiveSubscriptionStatus = resolvedSubscriptionStatus || subscriptionStatus;
  const isSubscriptionLoading = isLoadingSubscription || isResolvingSubscription;

  // Check if user has premium access (admins always have access)
  const isAdmin = adminService.isAdmin();
  const hasPremium = isAdmin || (!isSubscriptionLoading && effectiveSubscriptionStatus && !effectiveSubscriptionStatus.isFree && effectiveSubscriptionStatus.isActive);
  const isFreeModeLockedToFirstProbe = !isSubscriptionLoading && !hasPremium;

  const provider = (examData?.examProvider || 'telc').toLowerCase();
  const level    = (examData?.level || 'B1').toLowerCase();

  // In-progress answers survive refresh / tab close (cleared per part via reset).
  const trainingSessionKey = `logami:v1:training:reading:${auth.currentUser?.uid || 'anon'}:${provider}-${level}`;
  const restoredTraining = useMemo(() => readSession(trainingSessionKey), [trainingSessionKey]);

  const [samples, setSamples]       = useState([]);
  const [sampleIdx, setSampleIdx]   = useState(0);
  const [sampleData, setSampleData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [selectedPart, setSelectedPart] = useState(null); // null = hub
  const [allAnswers, setAllAnswers]     = useState(() => restoredTraining?.allAnswers ?? {});    // { partKey: { qId: answer } }
  const [checked, setChecked]           = useState(() => restoredTraining?.checked ?? {});    // { partKey: bool }
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [isMobileAnswerSheetOpen, setIsMobileAnswerSheetOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPartId, setHistoryPartId] = useState(null); // null = all parts
  const [pendingPart, setPendingPart] = useState(null);     // Teil chosen, sample not yet picked
  const [sessionScores, setSessionScores] = useState(() => restoredTraining?.sessionScores ?? {});   // { partKey: {correct,total} } graded at check time
  // Free-plan one-time usage flags (null while loading)
  const [freeAttempts, setFreeAttempts] = useState(null);

  useEffect(() => {
    writeSession(trainingSessionKey, { allAnswers, checked, sessionScores });
  }, [trainingSessionKey, allAnswers, checked, sessionScores]);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    freeAttemptService.getFreeAttempts(uid).then(setFreeAttempts);
  }, []);

  const formatSampleLabel = (label) =>
    String(label || '').replace(/^Probe\b/i, t('exam.training.probeLabel', { defaultValue: 'Test' }));

  // Keep free users anchored to Probe 1 even if state somehow points to later probes.
  useEffect(() => {
    if (!isFreeModeLockedToFirstProbe) return;
    if (sampleIdx > 0) {
      setSampleIdx(0);
      setSelectedPart(null);
    }
  }, [isFreeModeLockedToFirstProbe, sampleIdx]);

  // Load manifest
  useEffect(() => {
    setLoading(true); setError(null);
    fetch('/data/exam-training/lesen/index.json')
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(manifest => { setSamples(manifest?.[provider]?.[level] || []); setSampleIdx(0); })
      .catch(() => { setError('Keine Trainingsdaten gefunden.'); setLoading(false); });
  }, [provider, level]);

  // Load sample
  useEffect(() => {
    if (!samples.length) { setLoading(false); return; }
    const s = samples[sampleIdx];
    setLoading(true); setError(null);
    fetch(`/data/exam-training/lesen/${provider}/${level}/${s.id}.json`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setSampleData(normalizeTrainingSections(data)); setLoading(false); })
      .catch(() => { setError('Datei konnte nicht geladen werden.'); setLoading(false); });
  }, [samples, sampleIdx, provider, level]);

  const partKey = selectedPart !== null
    ? `${sampleIdx}-${selectedPart.sectionIndex}-${selectedPart.partIndex}`
    : null;

  const currentPart = selectedPart !== null && sampleData
    ? {
        ...(sampleData[selectedPart.sectionIndex]?.parts?.[selectedPart.partIndex] || {}),
        examProvider: (examData?.examProvider || 'TELC').toUpperCase(),
        level:        (examData?.level || 'B1').toUpperCase(),
      }
    : null;

  const partAnswers = partKey ? (allAnswers[partKey] || {}) : {};
  const isChecked   = partKey ? (checked[partKey]    || false) : false;

  useEffect(() => {
    setIsMobileAnswerSheetOpen(false);
  }, [partKey]);

  const handleAnswerChange = useCallback((_partId, newAnswers) => {
    if (!partKey) return;
    setAllAnswers(prev => ({ ...prev, [partKey]: newAnswers }));
  }, [partKey]);

  const handleCheckAnswers = useCallback(() => {
    if (!partKey) return;

    // Free plan: one graded attempt per Teil.
    if (isFreeModeLockedToFirstProbe && freeAttemptService.isSkillAttemptUsed(freeAttempts, 'reading', currentPart?.partId)) {
      setShowPremiumModal(true);
      return;
    }

    if (sw.running) sw.toggle();
    setIsMobileAnswerSheetOpen(false);
    setChecked(prev => ({ ...prev, [partKey]: true }));

    const uid = auth.currentUser?.uid;
    if (currentPart && selectedPart) {
      const graded = gradeReadingPart(currentPart, partAnswers);
      setSessionScores(prev => ({ ...prev, [partKey]: { correct: graded.correct, total: graded.total } }));
      if (!uid) return;
      if (isFreeModeLockedToFirstProbe && currentPart.partId) {
        freeAttemptService.markSkillAttempt(uid, 'reading', currentPart.partId);
        setFreeAttempts(prev => ({ ...(prev || {}), [`reading_${currentPart.partId}`]: true }));
      }
      const section = sampleData?.[selectedPart.sectionIndex];
      const sample = samples[sampleIdx];
      enhancedUserService.savePracticeAttempt(uid, {
        mode: 'reading',
        provider,
        level,
        sampleId: sample?.id,
        sampleLabel: sample?.label,
        sectionId: section?.sectionId,
        sectionName: section?.sectionName,
        partId: currentPart.partId,
        partNumber: currentPart.partNumber,
        partName: currentPart.partName,
        taskType: currentPart.taskType,
        questionType: currentPart.questionType,
        score: graded.correct,
        maxScore: graded.total,
        userAnswers: Object.fromEntries(graded.results.map(r => [r.questionId, r.userAnswer])),
        correctAnswers: Object.fromEntries(graded.results.map(r => [r.questionId, r.correctAnswer])),
        answers: graded.results,
        timeSpentSeconds: sw.elapsed,
      });
    }
  }, [partKey, sw, currentPart, partAnswers, sampleData, selectedPart, samples, sampleIdx, provider, level, isFreeModeLockedToFirstProbe, freeAttempts]);

  const handleResetPart = useCallback(() => {
    if (!partKey) return;
    // Free plan: the single attempt for this Teil is used, resetting would only lead to a blocked check.
    if (isFreeModeLockedToFirstProbe && freeAttemptService.isSkillAttemptUsed(freeAttempts, 'reading', currentPart?.partId)) {
      setShowPremiumModal(true);
      return;
    }
    setAllAnswers(prev => ({ ...prev, [partKey]: {} }));
    setChecked(prev => ({ ...prev, [partKey]: false }));
    sw.reset();
  }, [partKey, sw, isFreeModeLockedToFirstProbe, freeAttempts, currentPart]);

  const toggleFlagQuestion = useCallback((questionId) => {
    setFlaggedQuestions(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  }, []);

  const selectSampleForPendingPart = (i) => {
    if (i < 0 || i >= samples.length || pendingPart === null) return;

    // Free users can only open Probe 1.
    if (isFreeModeLockedToFirstProbe && i > 0) {
      setBlockedProbeNumber(i + 1);
      setShowPremiumModal(true);
      trackPaywallShown({ context: 'lesen' });
      return;
    }

    setSampleIdx(i);
    setSelectedPart(pendingPart);
    sw.reset();
  };

  const handleBack = useCallback(() => {
    if (selectedPart !== null) {
      setIsMobileAnswerSheetOpen(false);
      sw.reset();
      setSelectedPart(null);
      return;
    }
    const dest = onBack ? () => onBack() : () => navigate('/practice');
    if (isFreeModeLockedToFirstProbe) {
      setPendingExitNav(() => dest);
      setShowPremiumModal(true);
      trackPaywallShown({ context: 'lesen' });
      return;
    }
    dest();
  }, [selectedPart, onBack, navigate, sw, isFreeModeLockedToFirstProbe]);

  const openLessonStrategyForPart = useCallback((sectionId, part, partIndex) => {
    const lessonLink = buildLessonStrategyLink(provider, level, sectionId, part, partIndex);
    if (!lessonLink) return;
    navigate(lessonStrategyPath, {
      state: {
        dataUrl: lessonLink.dataUrl,
      },
    });
  }, [provider, level, navigate, lessonStrategyPath]);

  const computeScore = () => {
    if (!currentPart) return { correct: 0, total: 0 };
    return gradeReadingPart(currentPart, partAnswers);
  };

  const hubScore = (sIdx, secIdx, pIdx) => {
    const k    = `${sIdx}-${secIdx}-${pIdx}`;
    const part = sampleData?.[secIdx]?.parts?.[pIdx];
    if (!part || !checked[k]) return null;
    return gradeReadingPart(part, allAnswers[k] || {});
  };

  // ─── LOADING / ERROR ─────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className={`${ELEVATED_SURFACE} p-8 w-full max-w-sm text-center animate-fade-in-up`}>
        <LogamiLogoSpinner size="xl" className="mx-auto" decorative={false} alt="Logami loading" />
        <p className="mt-4 text-sm text-gray-600 font-semibold">Trainingsdaten werden geladen ...</p>
      </div>
    </div>
  );

  if (error || !samples.length) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className={`${ELEVATED_SURFACE} text-center max-w-sm w-full p-6 animate-fade-in-up`}>
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-semibold mb-2">{error || 'Noch keine Trainingsaufgaben verfügbar.'}</p>
        <p className="text-gray-400 text-sm mb-6">
          Für {provider.toUpperCase()} {level.toUpperCase()} gibt es noch keine Lesen-Trainingsdaten.
        </p>
        <button
          onClick={() => navigate('/practice')}
          className="min-h-[48px] inline-flex items-center justify-center px-4 py-2 rounded-xl border-2 border-black bg-black text-white font-bold text-sm hover:bg-gray-900 touch-manipulation transition-all duration-300"
        >
          {t('common.goBack')}
        </button>
      </div>
    </div>
  );

  if (showHistory) {
    return (
      <PracticeHistoryPanel
        mode="reading"
        provider={provider}
        level={level}
        onClose={() => setShowHistory(false)}
        filterAttempt={historyPartId ? (attempt) => attempt.partId === historyPartId : undefined}
      />
    );
  }

  // ─── SAMPLE SELECT (Teil chosen, Muster not yet) ─────────────────────────────
  if (pendingPart !== null && selectedPart === null) {
    const pendingPartData = sampleData?.[pendingPart.sectionIndex]?.parts?.[pendingPart.partIndex];
    return (
      <>
      <AhaUpsellModal
        open={showPremiumModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="lesen"
      />
      <SampleSelectView
        partName={pendingPartData?.partName || `Teil ${pendingPart.partIndex + 1}`}
        partTypeLabel={PART_TYPE_LABELS[pendingPartData?.taskType] || pendingPartData?.taskType}
        samples={samples}
        formatLabel={formatSampleLabel}
        isSampleLocked={(i) => isFreeModeLockedToFirstProbe && i > 0}
        getSessionScore={(i) => sessionScores[`${i}-${pendingPart.sectionIndex}-${pendingPart.partIndex}`] || null}
        getSessionProgress={(i) => {
          const k = `${i}-${pendingPart.sectionIndex}-${pendingPart.partIndex}`;
          if (checked[k]) return null;
          const answered = Object.values(allAnswers[k] || {}).filter(v => v !== null && v !== undefined && v !== '').length;
          return answered > 0 ? answered : null;
        }}
        onSelectSample={selectSampleForPendingPart}
        onBack={() => setPendingPart(null)}
        onOpenHistory={() => { setHistoryPartId(pendingPartData?.partId || null); setShowHistory(true); }}
      />
      </>
    );
  }

  // ─── PART VIEW ────────────────────────────────────────────────────────────────
  if (selectedPart !== null && currentPart) {
    const currentSection = sampleData?.[selectedPart.sectionIndex];
    const currentLessonLink = buildLessonStrategyLink(
      provider,
      level,
      currentSection?.sectionId,
      currentPart,
      selectedPart.partIndex
    );

    const score         = computeScore();
    const totalQ        = (currentPart.questions || []).filter(q => !q.isExample).length;
    const answeredCount = Object.values(partAnswers).filter(v => v !== null && v !== undefined).length;
    const answeredPct   = totalQ > 0 ? Math.round((answeredCount / totalQ) * 100) : 0;
    const pct           = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
    const hasObjectiveQuestions = (currentPart.questions || []).some(q => !q.isExample);
    const showAnswerSheetButton = !isChecked &&
      !LESEN_WRITING_TASK_TYPES.has(currentPart.taskType) &&
      (LESEN_ANSWER_SHEET_TASK_TYPES.has(currentPart.taskType) || hasObjectiveQuestions);
    const scoreColor    = pct >= 80 ? 'text-emerald-700 bg-emerald-100 border-emerald-300'
                        : pct >= 60 ? 'text-amber-700 bg-amber-100 border-amber-300'
                        :             'text-rose-700 bg-rose-100 border-rose-300';

    return (
      <div className="min-h-screen bg-white pb-16 sm:pb-20 animate-fade-in">
        <AhaUpsellModal
          open={showPremiumModal}
          onDismiss={handleAhaDismiss}
          onUpgrade={handleAhaUpgrade}
          context="lesen"
        />

        {/* Sticky header */}
        <div className="sticky top-0 z-30 bg-white/92 backdrop-blur-xl border-b border-black/10 shadow-sm safe-area-inset-top">
          <div className="w-full px-4 sm:px-6 pt-3 pb-3 sm:py-4">
            {/* On desktop, keep groups adjacent to avoid a large dead gap between title and progress. */}
            <div className="flex items-center justify-between lg:justify-start w-full gap-4 flex-wrap lg:flex-nowrap">
              
              {/* LEFT GROUP: Back button + Badge + Title */}
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="hidden sm:block">
                  <AnimatedBackButton onClick={handleBack} backLabel={t('common.goBack')} />
                </div>

                <div className="min-w-0 lg:max-w-[280px]">
                  <p className="inline-flex max-w-full px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] text-[#3A86FF] bg-[#3A86FF]/10 border border-[#3A86FF]/20 leading-tight truncate">
                    {currentSection?.sectionName}
                  </p>
                  <p className="font-space font-black text-gray-900 text-sm sm:text-base truncate mt-1">{currentPart.partName}</p>
                </div>
              </div>

              {/* Mobile timer readout, control remains in bottom nav */}
              <div className="lg:hidden ml-auto flex-shrink-0">
                <div
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-bold transition-colors duration-300 ${
                    sw.running
                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                      : 'bg-gray-50 border-black/10 text-gray-600'
                  }`}
                  aria-label="Timer"
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span className="font-mono tabular-nums">{sw.fmt(sw.elapsed)}</span>
                </div>
              </div>

              {/* RIGHT GROUP: Progress + Buttons + Timer + Lessons (hidden on mobile) */}
              <div className="hidden lg:flex min-w-0 flex-1 items-center gap-3">
                <div className={`${SOFT_SURFACE} flex items-center gap-3 px-3 py-2.5 min-w-[300px]`}>
                  {isChecked ? (
                    <>
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-bold text-sm flex-shrink-0 ${scoreColor}`}>
                        <CheckCircle className="w-4 h-4" />
                        {score.correct}/{score.total} · {pct}%
                      </div>

                      <div className="flex-1 min-w-[120px]">
                        <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500"
                            style={{ width: `${isChecked ? 100 : answeredPct}%` }}
                          />
                        </div>
                      </div>

                      <button
                        onClick={handleResetPart}
                        className="flex-shrink-0 min-h-[44px] inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 border-black/10 bg-white text-gray-700 font-bold text-sm hover:border-black/30 hover:bg-gray-50 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Nochmal
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="min-w-[112px] flex-shrink-0 text-center">
                        <p className="text-sm text-gray-600 leading-tight">
                          <span className="font-bold text-gray-900">{answeredCount}</span>/{totalQ} beantwortet
                        </p>
                        <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold mt-1">Fortschritt {answeredPct}%</p>
                      </div>

                      <div className="flex-1 min-w-[120px]">
                        <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500"
                            style={{ width: `${isChecked ? 100 : answeredPct}%` }}
                          />
                        </div>
                      </div>

                      <button
                        onClick={handleCheckAnswers}
                        disabled={answeredCount === 0}
                        className="flex-shrink-0 min-h-[44px] inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-black via-gray-900 to-[#1f3b63] text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_8px_20px_rgba(17,24,39,0.28)] active:scale-[0.98] touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Antworten prüfen
                      </button>
                    </>
                  )}
                </div>

                <div className="ml-auto flex items-center gap-3 flex-shrink-0">
                  <Stopwatch sw={sw} t={t} />

                  {currentLessonLink && (
                    <button
                      onClick={() => openLessonStrategyForPart(currentSection?.sectionId, currentPart, selectedPart.partIndex)}
                      className="flex-shrink-0 min-h-[48px] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#1f3b63]/20 bg-[#eef3fb] text-[#1f3b63] font-semibold text-sm hover:bg-[#e4edf9] hover:border-[#1f3b63]/35 transition-all duration-300 touch-manipulation whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/30"
                      aria-label={t('nav.lessons')}
                    >
                      <Lightbulb className="w-4 h-4" />
                      <span>{t('nav.lessons')}</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={`${SOFT_SURFACE} mt-3 px-3 py-3 sm:px-4 lg:hidden`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                {isChecked ? (
                  <>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-bold text-sm sm:flex-shrink-0 ${scoreColor}`}>
                      <CheckCircle className="w-4 h-4" />
                      {score.correct}/{score.total} · {pct}%
                    </div>

                    <div className="flex-1 min-w-[160px]">
                      <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500"
                          style={{ width: `${isChecked ? 100 : answeredPct}%` }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center sm:min-w-[140px] sm:flex-shrink-0">
                      <p className="text-sm text-gray-600">
                        <span className="font-bold text-gray-900">{answeredCount}</span>/{totalQ} beantwortet
                      </p>
                      <p className="text-[11px] text-gray-400 uppercase tracking-wide font-semibold">Fortschritt {answeredPct}%</p>
                    </div>

                    <div className="flex-1 min-w-[160px]">
                      <div className="w-full h-1.5 rounded-full bg-black/10 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#3A86FF] via-indigo-500 to-black transition-all duration-500"
                          style={{ width: `${isChecked ? 100 : answeredPct}%` }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Task content. PartDisplay while answering, TrainingResultsView after checking */}
        <div className="pt-1">
          {isChecked ? (
            <TrainingResultsView part={currentPart} answers={partAnswers} />
          ) : (
            <div className="animate-fade-in-up">
              <PartDisplay
                partData={currentPart}
                savedAnswers={partAnswers}
                onAnswerChange={handleAnswerChange}
                flaggedQuestions={flaggedQuestions}
                toggleFlagQuestion={toggleFlagQuestion}
                goToNextPart={() => {}}
                allParts={[]}
                timeRemaining={undefined}
                currentSection={currentSection}
                isWarning={false}
                isCritical={false}
                isMobileAnswerSheetOpen={isMobileAnswerSheetOpen}
                onCloseMobileAnswerSheet={() => setIsMobileAnswerSheetOpen(false)}
                enableReadingWordTools={true}
              />
            </div>
          )}
        </div>

        {/* Bottom Navigation. Exercise View (mobile only) */}
        <GrammarBottomNav
          buttons={[
            {
              id: 'back',
              icon: ArrowLeft,
              label: t('grammar.back', { defaultValue: 'Back' }),
              onClick: handleBack,
              color: '#3A86FF'
            },
            showAnswerSheetButton ? {
              id: 'answer-sheet',
              icon: ClipboardList,
              label: t('home.timer.answerSheet', { defaultValue: 'Answer sheet' }),
              onClick: () => setIsMobileAnswerSheetOpen(true),
              color: '#006D77',
              iconColor: '#006D77',
              labelColor: '#006D77',
              iconContainerStyle: { backgroundColor: 'rgba(0,109,119,0.1)' },
              showActiveIndicator: false,
            } : null,
            {
              id: 'check',
              icon: CheckCircle,
              label: 'Antworten prüfen',
              onClick: handleCheckAnswers,
              disabled: isChecked || answeredCount === 0,
              active: !isChecked,
              color: '#3A86FF'
            },
            {
              id: 'timer',
              icon: sw.running ? Pause : Play,
              label: sw.running ? 'Pause' : t('button.start', { defaultValue: 'Start' }),
              onClick: sw.toggle,
              color: '#3A86FF'
            },
            {
              id: 'reset',
              icon: RotateCcw,
              label: t('button.reset', { defaultValue: 'Reset' }),
              onClick: handleResetPart,
              color: '#3A86FF'
            }
          ].filter(Boolean)}
        />

      </div>
    );
  }

  // ─── HUB VIEW ────────────────────────────────────────────────────────────────
  const lesenSections  = (sampleData || []).filter(s => s.sectionId === 'reading');
  const grammarSections = (sampleData || []).filter(s => s.sectionId === 'grammar');

  const sectionGroups = [
    { key: 'reading', tone: 'blue', sections: lesenSections },
    { key: 'grammar', tone: 'emerald', sections: grammarSections },
  ].filter(group => group.sections.length > 0);

  const renderPartCard = (sectionId, sectionIndex, part, partIndex, tone = 'blue') => {
    const k        = `${sampleIdx}-${sectionIndex}-${partIndex}`;
    const answered = Object.keys(allAnswers[k] || {}).length;
    const total    = (part.questions || []).filter(q => !q.isExample).length;
    const isChkd   = checked[k] || false;
    const sc       = hubScore(sampleIdx, sectionIndex, partIndex);
    const isBlue = tone === 'blue';
    const lessonLink = buildLessonStrategyLink(provider, level, sectionId, part, partIndex);

    const hoverBorder = isBlue ? 'hover:border-[#3A86FF]/38' : 'hover:border-emerald-300';
    const hoverShadow = isBlue
      ? 'hover:shadow-[0_18px_34px_rgba(58,134,255,0.14)]'
      : 'hover:shadow-[0_18px_34px_rgba(16,185,129,0.14)]';
    const hoverTint = isBlue
      ? 'from-[#3A86FF]/10 via-white to-[#0F172A]/[0.03]'
      : 'from-emerald-500/10 via-white to-emerald-100/25';
    const progressPill = isBlue
      ? 'bg-[#3A86FF]/8 border-[#3A86FF]/18 text-[#275eb4]'
      : 'bg-emerald-50 border-emerald-200 text-emerald-700';
    const primaryCta = isBlue
      ? 'bg-gradient-to-r from-black via-[#0F172A] to-[#1F3B63] shadow-[0_10px_22px_rgba(15,23,42,0.2)]'
      : 'bg-gradient-to-r from-black via-[#10281D] to-[#065F46] shadow-[0_10px_22px_rgba(6,95,70,0.18)]';
    const scoreTextColor = sc?.correct === sc?.total
      ? 'text-emerald-700'
      : sc?.correct >= sc?.total * 0.6
        ? 'text-amber-700'
        : 'text-rose-700';

    const openPart = () => {
      // Sample (Muster) is picked on the next screen; the paywall check for
      // locked samples happens there in selectSampleForPendingPart.
      setPendingPart({ sectionIndex, partIndex });
    };

    const onCardKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openPart();
      }
    };

    return (
      <div
        key={partIndex}
        role="button"
        tabIndex={0}
        onClick={openPart}
        onKeyDown={onCardKeyDown}
        className={`lesen-task-card group relative w-full overflow-hidden rounded-2xl border-2 border-black/10 bg-white p-3.5 ${hoverBorder} ${hoverShadow} hover:-translate-y-1 active:scale-[0.995] transition-all duration-300 text-left touch-manipulation cursor-pointer animate-fade-in-up focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/30`}
        style={{ animationDelay: `${partIndex * 70}ms` }}
        aria-label={t('exam.training.start', { defaultValue: 'Start training' })}
      >
        <div className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${hoverTint} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

        <div className="relative z-10 flex items-start gap-3.5">
          <AnimatedTaskTypeIcon taskType={part.taskType} tone={tone} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-gray-900 text-sm sm:text-[15px] leading-tight">{part.partName || `Teil ${partIndex + 1}`}</p>
                <p className="mt-0.5 text-[11px] text-gray-500">{PART_TYPE_LABELS[part.taskType] || part.taskType}</p>
              </div>

              {isChkd && sc && (
                <span className={`hidden sm:inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${scoreTextColor} ${sc.correct === sc.total ? 'bg-emerald-50 border-emerald-200' : sc.correct >= sc.total * 0.6 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`}>
                  {sc.correct}/{sc.total}
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-black/10 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                {total} Aufgaben · {part.points} Pkt.
              </span>
              {answered > 0 && !isChkd && (
                <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${progressPill}`}>
                  {answered}/{total} beantwortet
                </span>
              )}
              {isChkd && sc && (
                <span className={`inline-flex sm:hidden items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${scoreTextColor} ${sc.correct === sc.total ? 'bg-emerald-50 border-emerald-200' : sc.correct >= sc.total * 0.6 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`}>
                  {sc.correct}/{sc.total} richtig
                </span>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2.5">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black text-white ${primaryCta}`}>
                {t('exam.training.start', { defaultValue: 'Start training' })}
                <ChevronRight className="lesen-task-icon-arrow h-3.5 w-3.5" />
              </span>

              {lessonLink && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openLessonStrategyForPart(sectionId, part, partIndex);
                  }}
                  className="min-h-[34px] inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-gray-500 transition-colors duration-200 hover:border-black/20 hover:text-gray-700 touch-manipulation"
                  aria-label={t('nav.lessons')}
                >
                  <Lightbulb className="h-3.5 w-3.5" />
                  <span>{t('nav.lessons')}</span>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-10 animate-fade-in">
      <style>{LESEN_TRAINING_CARD_STYLES}</style>
      <div className="max-w-6xl mx-auto px-4 py-5 sm:py-8">

        {/* Header */}
        <div className={`${ELEVATED_SURFACE} p-3.5 sm:p-[18px] mb-5 sm:mb-6 animate-fade-in-up`}>
          <div className="grid grid-cols-[auto_1fr_auto] items-start sm:items-center gap-3 sm:gap-4">
            <div className="justify-self-start hidden sm:block">
              <AnimatedBackButton onClick={handleBack} backLabel={t('common.goBack')} />
            </div>

            <div className="text-center">
              <div className="inline-block px-3 py-1 bg-black text-white text-[11px] font-bold tracking-widest uppercase rounded-full mb-1 animate-fade-in-up">
                Lesen Training
              </div>
              <h1 className="font-space text-2xl sm:text-4xl font-black text-gray-900 leading-tight">
                {(examData?.examProvider || 'TELC').toUpperCase()} {(examData?.level || 'B1').toUpperCase()}
              </h1>
            </div>

            <div className="hidden sm:block" />
          </div>

        </div>

        <div className={sectionGroups.length === 2 ? 'grid grid-cols-1 lg:grid-cols-2 gap-5' : 'space-y-5'}>
          {sectionGroups.map(group => (
            <div key={group.key} className="space-y-5">
              {group.sections.map((section, sectionLocalIdx) => {
                const sectionIndex = sampleData.indexOf(section);
                const isBlue = group.tone === 'blue';
                const dividerTone = isBlue ? 'via-[#3A86FF]/35' : 'via-emerald-400/45';
                const accentTone = isBlue ? 'bg-[#3A86FF]' : 'bg-emerald-500';
                return (
                  <div key={`${section.sectionId}-${sectionLocalIdx}`} className={`${ELEVATED_SURFACE} relative p-3.5 sm:p-4 animate-fade-in-up`}>
                    <div className={`absolute inset-x-0 top-0 h-[3px] rounded-t-2xl bg-gradient-to-r from-transparent ${dividerTone} to-transparent`} />

                    <div className="relative z-10 flex items-center gap-3 mb-3.5">
                      <div className={`h-px flex-1 bg-gradient-to-r from-transparent ${dividerTone} to-transparent`} />
                      <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 px-2">{section.sectionName}</span>
                      <div className={`h-px flex-1 bg-gradient-to-r from-transparent ${dividerTone} to-transparent`} />
                    </div>

                    <div className="relative z-10 space-y-2.5">
                      {(section.parts || []).map((part, partIndex) =>
                        renderPartCard(section.sectionId, sectionIndex, part, partIndex, group.tone)
                      )}
                    </div>

                    <div className="relative z-10 mt-3.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${accentTone}`} />
                      {t('exam.training.partsCount', { count: section.parts?.length || 0, defaultValue: '{{count}} parts' })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Navigation. Hub View (mobile only) */}
      <GrammarBottomNav
        buttons={[
          {
            id: 'back',
            icon: ArrowLeft,
            label: t('grammar.back', { defaultValue: 'Back' }),
            onClick: handleBack,
            color: '#3A86FF'
          },
          {
            id: 'practice',
            icon: BookOpen,
            label: t('nav.practice', { defaultValue: 'Practice' }),
            onClick: () => { if (isFreeModeLockedToFirstProbe) { setPendingExitNav(() => () => navigate('/practice')); setShowPremiumModal(true); trackPaywallShown({ context: 'lesen' }); } else navigate('/practice'); },
            active: true,
            color: '#3A86FF'
          },
          {
            id: 'home',
            icon: Home,
            label: t('nav.home', { defaultValue: 'Home' }),
            onClick: () => { if (isFreeModeLockedToFirstProbe) { setPendingExitNav(() => () => navigate('/dashboard')); setShowPremiumModal(true); trackPaywallShown({ context: 'lesen' }); } else navigate('/dashboard'); },
            color: '#3A86FF'
          }
        ]}
      />

      {/* Premium Block Modal */}
      <AhaUpsellModal
        open={showPremiumModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="lesen"
      />
    </div>
  );
};

export default LesenTrainingPage;
